import type { ReactElement } from "react"
import styled from "styled-components"
import { AUDIENCE_OPTIONS, CONTENT_TYPE_OPTIONS, type LibraryFilters, STATUS_OPTIONS } from "../filters"

/**
 * MLC's filter row, ported: content type, status, search
 * (`components/DigitalMediaUI.js`). Filtering is client-side over the pages
 * already loaded — see the note the sections render when a filter finds nothing —
 * rather than re-querying. `libraryV2` and `entitlements` both take a
 * `searchTerm`, so moving search server-side later is a hook change, not a
 * redesign.
 */
const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  background-color: ${({ theme }) => theme.color.surfaceSubtle};
`

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textSecondary};
  white-space: nowrap;
`

const Select = styled.select`
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xs}`};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.color.controlSurface};
  font-size: ${({ theme }) => theme.fontSize.sm};
`

const Search = styled.input`
  flex: 1 1 12rem;
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.color.controlSurface};
  font-size: ${({ theme }) => theme.fontSize.sm};
`

interface LibraryFilterBarProps {
  readonly filters: LibraryFilters
  readonly onChange: (filters: LibraryFilters) => void
}

export const LibraryFilterBar = ({ filters, onChange }: LibraryFilterBarProps): ReactElement => (
  <Bar data-qa-hook="digital-library-filters">
    <Field>
      <Label htmlFor="library-filter-type">Filter by</Label>
      <Select
        id="library-filter-type"
        value={filters.contentType}
        data-qa-hook="digital-library-filter-type"
        onChange={event => onChange({ ...filters, contentType: event.target.value as LibraryFilters["contentType"] })}
      >
        {CONTENT_TYPE_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>

    <Field>
      <Label htmlFor="library-filter-audience">Audience</Label>
      <Select
        id="library-filter-audience"
        value={filters.audience}
        data-qa-hook="digital-library-filter-audience"
        onChange={event => onChange({ ...filters, audience: event.target.value as LibraryFilters["audience"] })}
      >
        {AUDIENCE_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>

    <Field>
      <Label htmlFor="library-filter-status">Status</Label>
      <Select
        id="library-filter-status"
        value={filters.status}
        data-qa-hook="digital-library-filter-status"
        onChange={event => onChange({ ...filters, status: event.target.value as LibraryFilters["status"] })}
      >
        {STATUS_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>

    <Search
      type="search"
      aria-label="Search your library"
      placeholder="Search"
      value={filters.searchTerm}
      data-qa-hook="digital-library-search"
      onChange={event => onChange({ ...filters, searchTerm: event.target.value })}
    />
  </Bar>
)
